import { useEffect, useRef, useState } from "react"
import styled from "styled-components/macro"

import {
  CanvasColorsOCR,
  DragCoordinates,
  drawCanvas,
  drawRectangle,
  Coordinates,
  Orientation,
} from "../../utils/canvas"
import { ControlBar } from "./ControlBar"

interface DocumentViewerProps {
  documentId: number
  rectanglesToDraw: [coordinates: Coordinates, color: CanvasColorsOCR][] | null
  isHighlighted: boolean
  fullDocumentId?: number
  fullDocCount?: number | null
}

export const DocumentViewer = ({
  documentId,
  rectanglesToDraw,
  isHighlighted,
}: DocumentViewerProps) => {
  const documentImageSrc = `/documents/show_image?id=${documentId}`

  let canvasRef = useRef<HTMLCanvasElement>(null)
  const canvas = canvasRef?.current
  const emptyImage = new Image()
  const [image, setImage] = useState(emptyImage)
  const [imageStatus, setImageStatus] = useState<"initial" | "loaded">(
    "initial"
  )

  useEffect(() => {
    setImageStatus("initial")
    let newImage = new Image()
    newImage.onload = () => {
      setImageStatus("loaded")
    }
    newImage.src = documentImageSrc
    setImage(newImage)
  }, [documentId])

  // not used yet, keep for potential need to draw rectangles, see king-julian repo
  const intracom = {}

  const [scale, setScale] = useState(1)
  const [rotationAngle, setRotationAngle] = useState<Orientation>("0")

  const [previousCoordinates, setPreviousCoordinates] =
    useState<DragCoordinates>({
      x: 0,
      y: 0,
    })
  const [dragOffset, setDragOffset] = useState<DragCoordinates>({
    x: 0,
    y: 0,
  })
  const [dragOrigin, setDragOrigin] = useState<DragCoordinates | null>(null)
  const [dragEnabled, setDragEnabled] = useState(false)

  useEffect(() => {
    setRotationAngle("0")
    setPreviousCoordinates({
      x: 0,
      y: 0,
    })
    setDragOffset({
      x: 0,
      y: 0,
    })
  }, [documentId])

  useEffect(() => {
    if (!canvas || !image || !documentId || imageStatus !== "loaded") {
      return
    }

    drawCanvas({
      image,
      canvas,
      scale,
      rotationAngle: Number(rotationAngle),
      dragOffset,
    })
  }, [
    canvas,
    imageStatus,
    image.width,
    image.height,
    scale,
    rotationAngle,
    dragOffset,
  ])

  useEffect(() => {
    setTimeout(() => {
      setDragOrigin(null)
    }, 1000)
  }, [])

  useEffect(() => {
    if (!canvas || !intracom || !documentId || !rectanglesToDraw) return

    rectanglesToDraw.forEach(([coordinates, color]) => {
      drawRectangle({
        canvas,
        color,
        coordinates,
        rotation: rotationAngle,
        pixelGap: 2.5,
      })
    })
  }, [
    intracom,
    scale,
    rotationAngle,
    dragOffset,
    dragOrigin,
    dragEnabled,
    image,
    canvas,
  ])

  return (
    <HeightWrapper>
      <Wrapper>
        {Boolean(documentId) && (
          <Canvas
            isHighlighted={isHighlighted}
            ref={canvasRef}
            onMouseDown={() => {
              setDragEnabled(true)
              setDragOrigin(null)
            }}
            onMouseUp={() => {
              setDragEnabled(false)
              setDragOrigin(null)
            }}
            onMouseOut={() => {
              setDragEnabled(false)
              setDragOrigin(null)
            }}
            onMouseMove={(event) => {
              if (!dragEnabled) {
                return
              }
              if (!dragOrigin) {
                setDragOrigin({
                  x: event.clientY,
                  y: event.clientY,
                })
                setPreviousCoordinates({
                  x: event.clientX,
                  y: event.clientY,
                })
                return
              }
              const gapX = event.clientX - previousCoordinates.x
              const gapY = event.clientY - previousCoordinates.y

              setDragOffset({
                x: dragOffset.x + gapX,
                y: dragOffset.y + gapY,
              })
              setPreviousCoordinates({
                x: event.clientX,
                y: event.clientY,
              })
            }}
          />
        )}
      </Wrapper>
      {Boolean(documentId) && (
        <ControlBarWrapper>
          <ControlBar
            setScale={setScale}
            scale={scale}
            setRotationAngle={(rotation) => {
              setRotationAngle(String(rotation) as Orientation)
            }}
            rotationAngle={Number(rotationAngle)}
          />
        </ControlBarWrapper>
      )}
    </HeightWrapper>
  )
}

const Canvas = styled.canvas<{ isHighlighted: boolean }>`
  width: 100%;
  cursor: grab;
  opacity: ${({ isHighlighted }) => (isHighlighted ? `0.7` : `1`)};
  transition: 0.5s opacity ease-in-out;
`
const HeightWrapper = styled.div`
  height: 100%;
`
const Wrapper = styled.div`
  max-height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
`

const ControlBarWrapper = styled.div`
  position: sticky;
  bottom: 8rem;
`
